import { EXCEPTION_KIND_LABEL, type ExceptionKind, FEE_LABEL, type InvoiceRow } from '@serievo/contracts';
import { Alert, Button, Card, Descriptions, Drawer, Empty, Flex, Input, Segmented, Skeleton, Table, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { formatInt } from '../api/client';
import { useInvoiceDetail, useInvoices } from '../api/reports';
import { Money, periodLabel, PlatformTag, viDate } from '../components/ui';
import { useWork } from '../features/context/WorkContext';

const KIND: Record<InvoiceRow['kind'], { label: string; color: string }> = {
  ORIGINAL: { label: 'Gốc', color: 'blue' },
  ADJUSTMENT: { label: 'Điều chỉnh', color: 'orange' },
  REPLACEMENT: { label: 'Thay thế', color: 'purple' },
};

/** Quản lý hóa đơn đầu ra: lọc theo loại, tìm theo số HĐ / mã đơn, xem chi tiết kèm đơn và phí sàn. */
export function InvoicesPage() {
  const w = useWork();
  const [kind, setKind] = useState<string>('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const list = useInvoices(w, { kind: kind || undefined, q: q || undefined });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.data.items) ?? [], [list.data]);
  const totals = list.data?.pages[0]?.data.totals;

  return (
    <div className="page">
      <Typography.Title level={4} className="page-title">
        Quản lý hóa đơn · {periodLabel(w.period)}
      </Typography.Title>
      <Card size="small">
        <Flex wrap gap={8} justify="space-between" align="center" style={{ marginBottom: 12 }}>
          <Segmented
            value={kind}
            onChange={(v) => setKind(String(v))}
            options={[
              { value: '', label: 'Tất cả' },
              { value: 'ORIGINAL', label: 'Hóa đơn gốc' },
              { value: 'ADJUSTMENT', label: 'Điều chỉnh' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="Số hóa đơn hoặc mã đơn sàn"
            onSearch={(v) => setQ(v.trim())}
            style={{ width: 280, maxWidth: '100%' }}
            aria-label="Tìm hóa đơn"
          />
        </Flex>
        {totals && (
          <Flex wrap gap={24} className="totals-strip">
            <span>
              Số hóa đơn: <b className="num">{formatInt(totals.count)}</b>
            </span>
            <span>
              Chưa thuế: <Money v={totals.preTax} strong />
            </span>
            <span>
              Thuế GTGT: <Money v={totals.vatAmount} strong />
            </span>
            <span>
              Tổng: <Money v={totals.total} strong />
            </span>
          </Flex>
        )}
        {list.error && <Alert type="error" showIcon message={list.error.message} />}
        <Table<InvoiceRow>
          size="small"
          rowKey="id"
          loading={list.isPending}
          dataSource={items}
          pagination={false}
          scroll={{ x: 920 }}
          onRow={(r) => ({ onClick: () => setOpen(r.id), style: { cursor: 'pointer' } })}
          columns={[
            { title: 'Ký hiệu', dataIndex: 'serial', width: 90 },
            { title: 'Số', dataIndex: 'number', width: 80, className: 'num' },
            { title: 'Ngày lập', dataIndex: 'issuedDate', width: 100, render: viDate },
            {
              title: 'Loại',
              dataIndex: 'kind',
              width: 100,
              render: (k: InvoiceRow['kind'], r) => (
                <>
                  <Tag color={KIND[k].color}>{KIND[k].label}</Tag>
                  {r.status === 'CANCELLED' && <Tag color="red">Đã hủy</Tag>}
                </>
              ),
            },
            { title: 'Sàn', dataIndex: 'platform', width: 90, render: (p: InvoiceRow['platform']) => <PlatformTag p={p} /> },
            { title: 'Mã đơn', dataIndex: 'orderSn', ellipsis: true },
            { title: 'Chưa thuế', dataIndex: 'preTax', align: 'right', render: (v: string) => <Money v={v} /> },
            { title: 'Thuế GTGT', dataIndex: 'vatAmount', align: 'right', render: (v: string) => <Money v={v} /> },
            { title: 'Tổng tiền', dataIndex: 'total', align: 'right', render: (v: string) => <Money v={v} strong /> },
          ]}
        />
        {list.hasNextPage && (
          <Flex justify="center" style={{ marginTop: 12 }}>
            <Button onClick={() => void list.fetchNextPage()} loading={list.isFetchingNextPage}>
              Tải thêm
            </Button>
          </Flex>
        )}
      </Card>
      <InvoiceDrawer id={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function InvoiceDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const w = useWork();
  const q = useInvoiceDetail(w, id);
  const d = q.data;
  return (
    <Drawer open={!!id} onClose={onClose} width={560} title={d ? `Hóa đơn ${d.serial} số ${d.number}` : 'Chi tiết hóa đơn'}>
      {q.isPending && <Skeleton active />}
      {q.error && <Alert type="error" showIcon message={q.error.message} />}
      {d && (
        <Flex vertical gap={16}>
          <Descriptions
            size="small"
            column={1}
            bordered
            items={[
              { key: 'k', label: 'Loại', children: <Tag color={KIND[d.kind].color}>{KIND[d.kind].label}</Tag> },
              { key: 'd', label: 'Ngày lập', children: `${viDate(d.issuedDate)} (${new Date(d.issuedAt).toLocaleTimeString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })})` },
              { key: 'b', label: 'Người mua', children: d.buyerName },
              { key: 'c', label: 'Mã của CQT', children: <span className="num">{d.cqtCode ?? '—'}</span> },
              { key: 'p', label: 'Chưa thuế', children: <Money v={d.preTax} /> },
              { key: 'r', label: 'Thuế suất', children: `${Number(d.vatRate) * 100}%` },
              { key: 'v', label: 'Thuế GTGT', children: <Money v={d.vatAmount} /> },
              { key: 't', label: 'Tổng thanh toán', children: <Money v={d.total} strong /> },
              ...(d.adjusts
                ? [{ key: 'a', label: 'Điều chỉnh cho', children: `${d.adjusts.serial} số ${d.adjusts.number} ngày ${viDate(d.adjusts.issuedDate)}` }]
                : []),
              ...(d.adjustments.length
                ? [
                    {
                      key: 'aa',
                      label: 'Đã điều chỉnh bởi',
                      children: d.adjustments.map((a) => `${a.serial} số ${a.number}`).join(', '),
                    },
                  ]
                : []),
            ]}
          />
          {d.exceptions.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Đối soát R1"
              description={d.exceptions.map((e) => (
                <Tag key={e.id} color={e.status === 'OPEN' ? 'red' : 'default'}>
                  {EXCEPTION_KIND_LABEL[e.kind as ExceptionKind] ?? e.kind} · {e.status === 'OPEN' ? 'đang mở' : e.status === 'CLOSED' ? 'đã đóng' : 'đã khớp'}
                </Tag>
              ))}
            />
          )}
          <Card size="small" title="Đơn hàng trên sàn">
            {d.order ? (
              <Descriptions
                size="small"
                column={1}
                items={[
                  { key: 's', label: 'Mã đơn', children: <><PlatformTag p={d.platform} /> <span className="num">{d.order.orderSn}</span></> },
                  { key: 'st', label: 'Trạng thái', children: d.order.status === 'RETURNED' ? 'Trả hàng' : d.order.status === 'COMPLETED' ? 'Hoàn thành' : 'Đã hủy' },
                  { key: 'dd', label: 'Giao thành công', children: viDate(d.order.deliveredDate) },
                  { key: 'g', label: 'Tiền hàng (gồm VAT)', children: <Money v={d.order.grossAmount} /> },
                  { key: 'i', label: 'Hàng hóa', children: d.order.itemSummary },
                ]}
              />
            ) : (
              <Empty description="Không tìm thấy đơn sàn" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
          <Card size="small" title="Dòng phí settlement của đơn">
            <Table
              size="small"
              rowKey={(r) => `${r.feeCode}-${r.settledDate}`}
              pagination={false}
              dataSource={d.settlement}
              columns={[
                { title: 'Ngày quyết toán', dataIndex: 'settledDate', render: viDate },
                { title: 'Khoản', dataIndex: 'feeCode', render: (c: string) => FEE_LABEL[c] ?? c },
                { title: 'Số tiền', dataIndex: 'amount', align: 'right', render: (v: string) => <Money v={v} /> },
              ]}
            />
          </Card>
        </Flex>
      )}
    </Drawer>
  );
}
